import { Col, Row, Tabs, Typography } from "antd";
import React, { createContext, useMemo, useState } from "react";
import {
  AiOutlineBarcode,
  AiOutlineCar,
  AiOutlineDeliveredProcedure,
  AiOutlineFile,
  AiOutlineInfoCircle,
} from "react-icons/ai";
import { FaWeightHanging } from "react-icons/fa";
import { GrStakeholder } from "react-icons/gr";
import { HiOutlineCash } from "react-icons/hi";
import { VscReferences } from "react-icons/vsc";
import { LuFileStack } from "react-icons/lu";
import { useLocation } from "react-router-dom";
import { IOperationContext } from "../../../../model/ContextModal";
import { useBookingStore } from "../../../../stores/BookingStore";
import { useOperationStore } from "../../../../stores/OperationStore";
import {
  AJEX_KSA_FFD_CCN,
  CASH,
  CASH_ID,
  CountryEnum,
  Employee,
  MARKEN_CUSTOMER,
  MARKEN_CUSTOMER_ACCOUNT_2,
  UserEnum,
  isAccessible,
  LIFE_SCIENCE
} from "../../../../utils/app-common";
import { convertDateFromMillis } from "../../../../utils/luxon";
import BackButtonComponent from "../../../CommonComponent/BackButtonComponent";
import BarCodesComponent from "./CodesComponent/BarCodeComponent";
import { HoldAtTerminalComponentLoadable } from "./HoldAtTerminalComponent";
import MarkenFormComponent from "./MarkenFormComponent/MarkenFormComponent";
import { ProofOfDeliveryBookingTableComponentLoadable } from "./POD";
import { PodWaybillsComponentLoadable } from "./PODDispatch";
import SuspenseComponent from "../../../CommonComponent/SuspenseComponent";
const { Title, Text } = Typography;

const OperationDetailsComponentLoadable = React.lazy(
  () => import("./BookingDetail")
);
const PieceBarCodeComponentLoadable = React.lazy(
  () => import("./PieceBarcodeComponent/PieceBarCodeComponent")
);
const CashComponentLoadable = React.lazy(() => import("./CashComponent"));
const OperationAssignDriverComponentLoadable = React.lazy(
  () => import("./OperationAssignDriverComponent")
);
const VolumetricWeightTableComponentLoadable = React.lazy(
  () => import("./Volumetric/VolumetricWeightTableComponent")
);
const ManifestComponentLoadable = React.lazy(() => import("./Manifest"));
const ReferenceTabComponentLoadable = React.lazy(
  () => import("./ReferenceTab")
);
const OperationFilesComponentLoadable = React.lazy(() => import("./Files"));
const SubWaybillsLoadable = React.lazy(() => import("./SubWaybills"));

export const OperationContext = createContext<IOperationContext | null>(null);
const OperationComponent = () => {
  const ClearStore = useBookingStore((state) => state?.ClearStore);
  const ClearOperationStoreAction = useOperationStore(
    (state) => state?.ClearOperationStoreAction
  );
  const { state } = useLocation() || {};
  const {
    bookingCode,
    waybillNo,
    id,
    isPickedup,
    billType,
    billTypeId,
    originalWaybillNo,
    isRto,
    productName,
    bookingDate,
    deliveryDate,
    pickupDate,
    rtoWaybill,
    customerAccount,
    isIrs,
  } = state || {};
  const enableLSBarcodeTab = productName?.includes(LIFE_SCIENCE);


  const isManifestor = isAccessible([
    UserEnum.MANIFESTER,
    UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,
    UserEnum.SALES_MANAGER,
    UserEnum.SALES_DIRECTOR,
    UserEnum.FINANCE,
    UserEnum.DATA_LOGGER,
  ]);

  const hasManifestDetailsAccess = isAccessible(
    [
      UserEnum.MANAGER,
      UserEnum.MANIFESTER,
      UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,
      UserEnum.OPERATION_SUPERVISOR_CASH_COLLECTOR,
      UserEnum.OPERATION_SUPERVISOR,
      UserEnum.OPS_MANAGER,
      UserEnum.OPERATION_SUPERVISOR,
      UserEnum.CUSTOMER_SERVICE,
      UserEnum.FINANCE,
      UserEnum.FINANCE_CONTROLLER,
      UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
    ],
    [CountryEnum.KSA, CountryEnum.UAE]
  );
  const hasManifestationDetailsAccessByEmployeeId = isAccessible(
    [Employee.HAJJAJ, Employee.IMATHUL_RAFI_KSA, Employee.UDAYA_MOGER],
    [CountryEnum.KSA],
    true
  );
  const isCustomerService = isAccessible([UserEnum.CUSTOMER_SERVICE]);
  const isTabUnAccessible = isAccessible([
    UserEnum.CUSTOMER_SERVICE,
    UserEnum.FINANCE,
    UserEnum.SALES_MANAGER,
    UserEnum.SALES_DIRECTOR,
    UserEnum.DATA_LOGGER,
    UserEnum.FINANCE_CONTROLLER,
  ]);

  let key: string;

  switch (true) {
    case isManifestor:
      key = "2";
      break;
    case isCustomerService:
      key = "5";
      break;
    default:
      // Set default value if none of the conditions are met
      key = "1";
      break;
  }
  const [activeKey, setActiveKey] = useState(key);

  const tabs = useMemo(() => {
    return [
      (isManifestor ? !isManifestor : !isTabUnAccessible) && {
        id: "1",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>
              <AiOutlineCar size={18} />
            </Col>
            <Col>Assign Drivers</Col>
          </Row>
        ),
        key: "1",
        children: (
          <SuspenseComponent>
            <OperationAssignDriverComponentLoadable />
          </SuspenseComponent>
        ),
      },
      !isCustomerService && {
        id: "2",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col className="">{<AiOutlineInfoCircle size={18} />}</Col>
            <Col>Booking</Col>
          </Row>
        ),
        key: "2",
        children: (
          <SuspenseComponent>
            <OperationDetailsComponentLoadable />
          </SuspenseComponent>
        ),
      },
      isIrs && {
        id: "15",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col className="">{<LuFileStack size={18} />}</Col>
            <Col>Sub Waybills</Col>
          </Row>
        ),
        key: "15",
        children: (
          <SuspenseComponent>
            <SubWaybillsLoadable />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible && {
        id: "3",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineFile size={18} />}</Col>
            <Col>Files</Col>
          </Row>
        ),
        key: "3",
        children: (
          <SuspenseComponent>
            <OperationFilesComponentLoadable />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible && {
        id: "4",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<FaWeightHanging size={18} />}</Col>
            <Col>Volumetric</Col>
          </Row>
        ),
        key: "4",
        children: (
          <SuspenseComponent>
            <VolumetricWeightTableComponentLoadable />
          </SuspenseComponent>
        ),
      },
      (enableLSBarcodeTab ? !enableLSBarcodeTab : !isTabUnAccessible) && {
        id: "6",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineBarcode size={18} />}</Col>
            <Col>Piece Barcode</Col>
          </Row>
        ),
        key: "6",
        children: (
          <SuspenseComponent>
            <PieceBarCodeComponentLoadable />
          </SuspenseComponent>
        ),
      },
      enableLSBarcodeTab && {
        id: "10",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineDeliveredProcedure size={18} />}</Col>
            <Col>Barcode</Col>
          </Row>
        ),
        disabled: false,
        key: "10",
        children: (
          <SuspenseComponent>
            <BarCodesComponent />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible && {
        id: "7",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<VscReferences size={18} />}</Col>
            <Col>Costing module</Col>
          </Row>
        ),
        disabled: false,
        key: "7",
        children: (
          <SuspenseComponent>
            <ReferenceTabComponentLoadable />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible && {
        id: "8",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineDeliveredProcedure size={18} />}</Col>
            <Col>Proof of Delivery</Col>
          </Row>
        ),
        disabled: false,
        key: "8",
        children: (
          <SuspenseComponent>
            <ProofOfDeliveryBookingTableComponentLoadable
              isOperation={true}
              waybillNumber={waybillNo}
              bookingId={id?.toString()}
            />
          </SuspenseComponent>
        ),
      },
      isPickedup &&
      (hasManifestDetailsAccess ||
        hasManifestationDetailsAccessByEmployeeId) && {
        id: "5",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineInfoCircle size={18} />}</Col>
            <Col>Manifest Details</Col>
          </Row>
        ),
        key: "5",
        children: (
          <SuspenseComponent>
            <ManifestComponentLoadable />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible &&
      ([CASH].includes(billType) || [CASH_ID].includes(billTypeId)) && {
        id: "9",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>
              <HiOutlineCash size={18} />
            </Col>
            <Col>Cash</Col>
          </Row>
        ),
        key: "9",
        children: (
          <SuspenseComponent>
            <CashComponentLoadable />
          </SuspenseComponent>
        ),
      },

      !isTabUnAccessible && {
        id: "11",
        label: (
          <Row justify="center" align={"middle"} gutter={4}>
            <Col>{<AiOutlineDeliveredProcedure size={18} />}</Col>
            <Col>POD Waybills</Col>
          </Row>
        ),
        disabled: false,
        key: "11",
        children: (
          <SuspenseComponent>
            <PodWaybillsComponentLoadable />
          </SuspenseComponent>
        ),
      },
      (customerAccount === MARKEN_CUSTOMER || customerAccount === MARKEN_CUSTOMER_ACCOUNT_2 || customerAccount === AJEX_KSA_FFD_CCN) && {
        id: "12",
        label: (
          <Row justify="center" align={"middle"} gutter={[0, 0]}>
            <Col>{<AiOutlineDeliveredProcedure size={18} />}</Col>
            <Col>Marken Form</Col>
          </Row>
        ),
        disabled: false,
        key: "12",
        children: (
          <SuspenseComponent>
            <MarkenFormComponent />
          </SuspenseComponent>
        ),
      },
      !isTabUnAccessible && {
        id: "13",
        label: (
          <Row justify="center" align={"middle"} gutter={[0, 0]}>
            <Col>{<GrStakeholder size={18} />}</Col>
            <Col>Hold at terminal</Col>
          </Row>
        ),
        disabled: false,
        key: "13",
        children: (
          <SuspenseComponent>
            <HoldAtTerminalComponentLoadable />
          </SuspenseComponent>
        ),
      },
    ];
  }, [
    billType,
    billTypeId,
    enableLSBarcodeTab,
    hasManifestDetailsAccess,
    isTabUnAccessible,
    isManifestor,
    isPickedup,
    id,
    waybillNo,
    isCustomerService,
    customerAccount,
    hasManifestationDetailsAccessByEmployeeId,
  ]);

  const onKeyChange = (key: string) => {
    setActiveKey(key);
  };

  const contextValues = useMemo(
    () => ({
      setActiveKey,
      ...state,
    }),
    [state]
  );

  const onTabClick = () => {
    ClearStore();
    ClearOperationStoreAction();
  };

  return (
    <OperationContext.Provider value={contextValues}>
      <Row justify={"space-between"}>
        <BackButtonComponent />
      </Row>
      <Row gutter={5}>
        <Col xxl={4} xl={3} lg={8} md={8}>
          <Title level={5} type="secondary">
            Booking Code : <Text mark> {bookingCode}</Text>
          </Title>
        </Col>
        <Col xxl={4} xl={3} lg={8} md={8}>
          <Title level={5} type="secondary">
            {" "}
            Airway Bill Number :<Text mark> {waybillNo}</Text>
          </Title>
        </Col>
        <Col xxl={3} xl={4} lg={8} md={8} hidden={!bookingDate}>
          <Title level={5} type="secondary">
            {" "}
            Booking Date :
            <Text mark> {convertDateFromMillis(bookingDate)}</Text>
          </Title>
        </Col>
        <Col xxl={4} xl={5} lg={8} md={6} hidden={!pickupDate}>
          <Title level={5} type="secondary">
            {" "}
            Actual Pickup Date :
            <Text mark> {convertDateFromMillis(pickupDate)} </Text>
          </Title>
        </Col>
        <Col xxl={4} xl={6} lg={8} md={6} hidden={!deliveryDate}>
          <Title level={5} type="secondary">
            {" "}
            Actual Delivery Date :{" "}
            <Text mark> {convertDateFromMillis(deliveryDate)}</Text>
          </Title>
        </Col>
        <Col xxl={4} xl={6} lg={8} md={8} hidden={!isRto}>
          <Title level={5} type="secondary">
            Original Waybill Number : <Text mark> {originalWaybillNo}</Text>
          </Title>
        </Col>
        <Col xxl={4} xl={6} lg={8} md={8} hidden={!rtoWaybill}>
          <Title level={5} type="secondary">
            RTO Waybill Number : <Text mark> {rtoWaybill}</Text>
          </Title>
        </Col>
      </Row>
      <Tabs
        size={"middle"}
        type="line"
        destroyInactiveTabPane
        defaultActiveKey={activeKey}
        activeKey={activeKey}
        onChange={onKeyChange}
        onTabClick={onTabClick}
        items={tabs}
      />
    </OperationContext.Provider>
  );
};

export default OperationComponent;
